/**
 * Undo/redo entries for the canvas actions (cs3 CONTRACT §2.9). Each builder answers a
 * `HistoryEntryInput` whose steps are the action's inverse and its replay.
 *
 * Rule: a step calls the SERVICE functions, never a hook. A hook records history, and an inverse
 * that recorded would undo itself. After a step the caches are refreshed here, so a hook has
 * nothing left to do when its action is replayed.
 */
import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "../queryKeys";
import { updateStoryMeta } from "../dataStories/storiesApi";
import {
  updateCanvasItemSummary,
  updateStoryNarrative,
  updateCanvasItemNarrative,
  updateStorySummary,
} from "../dataStories/narrativeApi";
import {
  createStoryFilter,
  deleteStoryFilter,
  patchStoryFilter,
  rewindStoryFilters,
} from "./storyFiltersApi";
import {
  deleteCanvasItem,
  patchCanvasItem,
  positionUpdateFor,
  postStoryEdit,
  repinCanvasItem,
  updateCanvasContentItem,
  updateCanvasItemSource,
  updateCanvasItemVisibility,
  updateCanvasItemsPositions,
  waitStoryEdit,
} from "./storyCanvasApi";
import { toCompanionFields } from "./canvasItemMappers";
import { refreshStoryItems, placeStoryItems } from "../../hooks/storyItemsCache";
import type { ApiVersion } from "../api";
import type { HistoryEntryInput } from "../canvasHistory/canvasHistory.types";
import type {
  CanvasContentItemUpdateRequestApi,
  CanvasItemBulkPositionItem,
  CanvasItemFullApi,
  CanvasItemListResponseApi,
  CanvasItemPatchRequestApi,
  CanvasItemSourceUpdateRequestApi,
  CanvasItemStubApi,
  CanvasItemVisibilityUpdateRequest,
  CanvasManifestApi,
  CanvasWidgetApi,
  ItemPositionApi,
} from "./storyCanvas.types";
import type {
  StoryFilterApi,
  StoryFilterCreateRequestApi,
  StoryFilterListResponseApi,
  StoryFilterMutationResponseApi,
  StoryFilterPatchRequestApi,
} from "./storyFilters.types";
import type { StoryMetaApi } from "../dataStories/storiesApi.types";

/**
 * Re-pin kill switch (cs3 risk 2). On the new path a delete is an unpin and Story Manager keeps
 * the canvas row, so undo re-pins the same id. If the live check ever says otherwise, set
 * REACT_APP_NEW_PATH_DELETE_UNDOABLE=off (or false) at build time: deleting then becomes a barrier
 * on the new path too, with no other change. Unset, it is on.
 */
export const isNewPathDeleteUndoable = (): boolean => {
  const raw = (process.env.REACT_APP_NEW_PATH_DELETE_UNDOABLE ?? "").trim().toLowerCase();
  return raw !== "off" && raw !== "false";
};

export interface InverseDeps {
  queryClient: QueryClient;
  workspaceId: string;
  storyId: string;
  /** "v2": the new path (Story Manager items). */
  version?: ApiVersion;
}

export const isNewPath = (version: ApiVersion | undefined): boolean => version === "v2";

/** An action that cannot be undone: recording it clears both stacks. */
export const barrierEntry = (label: string): HistoryEntryInput => ({ label, undo: null, redo: null });

// ─── Caches ──────────────────────────────────────────────────────────────────

/** Everything a replayed action can have changed on the canvas, read again. */
export const refreshCanvas = (deps: InverseDeps, itemId?: string): void => {
  const { queryClient, workspaceId, storyId } = deps;
  void queryClient.invalidateQueries({ queryKey: queryKeys.stories.canvasManifest(workspaceId, storyId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.stories.dataSources(workspaceId, storyId) });
  refreshStoryItems(queryClient, storyId, itemId);
  if (itemId !== undefined) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.stories.canvasWidget(workspaceId, storyId, itemId) });
  }
};

/** Positions the server confirmed, written into both the manifest and the summary list. */
export const applyPositionStubs = (deps: InverseDeps, stubs: readonly CanvasItemStubApi[]): void => {
  const { queryClient, workspaceId, storyId } = deps;
  queryClient.setQueryData<CanvasManifestApi>(queryKeys.stories.canvasManifest(workspaceId, storyId), (prev) => {
    if (!prev) return prev;
    const byId = new Map(stubs.map((stub) => [stub.item_id, stub]));
    return {
      ...prev,
      items: prev.items.map((stub) => {
        const updated = byId.get(stub.item_id);
        return updated ? { ...stub, position: updated.position, status: updated.status ?? stub.status } : stub;
      }),
    };
  });
  placeStoryItems(queryClient, storyId, stubs);
};

/** The geometry the caches hold for each id now, from the manifest and else the summary list. */
export const readCachedPositions = (
  queryClient: QueryClient,
  workspaceId: string,
  storyId: string,
  itemIds: readonly string[],
): Map<string, ItemPositionApi> => {
  const manifest = queryClient.getQueryData<CanvasManifestApi>(queryKeys.stories.canvasManifest(workspaceId, storyId));
  const summaries = queryClient.getQueryData<CanvasItemListResponseApi>(queryKeys.storyItems.summaries(storyId));
  const found = new Map<string, ItemPositionApi>();
  itemIds.forEach((id) => {
    const stub = manifest?.items.find((item) => item.item_id === id);
    if (stub?.position) {
      found.set(id, { ...stub.position });
      return;
    }
    const row = summaries?.items.find((item) => item.id === id);
    if (row) {
      found.set(id, {
        x: row.pos_x, y: row.pos_y, w: row.pos_w, h: row.pos_h,
        ...(row.pos_cols != null ? { cols: row.pos_cols } : {}),
        ...(row.pos_h_fixed != null ? { h_fixed: row.pos_h_fixed } : {}),
      });
    }
  });
  return found;
};

/** The widget the canvas holds for an item: the v1 widget read, else the v2 item's legacy `content`. */
export const readCachedWidget = (deps: InverseDeps, itemId: string): CanvasWidgetApi | undefined => {
  const { queryClient, workspaceId, storyId } = deps;
  return (
    queryClient.getQueryData<CanvasWidgetApi>(queryKeys.stories.canvasWidget(workspaceId, storyId, itemId)) ??
    queryClient.getQueryData<CanvasItemFullApi>(queryKeys.storyItems.detail(storyId, itemId))?.content ??
    undefined
  );
};

// ─── Row 1: move / resize ────────────────────────────────────────────────────

const sendPositions = async (deps: InverseDeps, items: CanvasItemBulkPositionItem[]): Promise<void> => {
  const stubs = await updateCanvasItemsPositions(deps.workspaceId, deps.storyId, items, deps.version);
  applyPositionStubs(deps, stubs);
};

/**
 * One flushed batch of tile moves. `before` is what each tile held when the gesture began; a tile
 * with no known geometry is left out of the undo. Answers null when nothing can be restored.
 */
export const positionsEntry = (
  deps: InverseDeps,
  batch: readonly CanvasItemBulkPositionItem[],
  before: ReadonlyMap<string, ItemPositionApi>,
): HistoryEntryInput | null => {
  const known = batch.filter((item) => before.has(item.item_id));
  if (known.length === 0) return null;
  const resized = known.some((item) => {
    const old = before.get(item.item_id) as ItemPositionApi;
    return (item.pos_w != null && item.pos_w !== old.w) || (item.pos_h != null && item.pos_h !== old.h);
  });
  const released = known.every((item) => item.pos_h_fixed === false && before.get(item.item_id)?.h_fixed === true);
  const label = resized ? "Resize tile" : released ? "Fit tile to content" : known.length > 1 ? `Move ${known.length} tiles` : "Move tile";
  const ids = known.map((item) => item.item_id);
  const resolve = (resolver: (id: string) => string, rows: CanvasItemBulkPositionItem[]) =>
    rows.map((row) => ({ ...row, item_id: resolver(row.item_id) }));
  return {
    label,
    itemIds: ids,
    undo: (ctx) => sendPositions(deps, resolve(ctx.resolve, known.map((item) => positionUpdateFor(item.item_id, before.get(item.item_id) as ItemPositionApi)))),
    redo: (ctx) => sendPositions(deps, resolve(ctx.resolve, known as CanvasItemBulkPositionItem[])),
  };
};

// ─── Rows 2, 3 (and 4, 14 for S1/S5): add / delete ───────────────────────────

export interface AddedItemArgs {
  label: string;
  itemId: string;
  /** Where it was placed: a redo on the new path re-pins it there. */
  position: ItemPositionApi | null;
  /**
   * Old path only: a delete there is a soft delete with no restore, so a redo posts the add again
   * and answers the new id.
   */
  recreate?: () => Promise<string>;
}

/** Undo = delete the new tile; redo = re-pin it (new path) or add it again (old path). */
export const addedItemEntry = (deps: InverseDeps, args: AddedItemArgs): HistoryEntryInput => ({
  label: args.label,
  itemIds: [args.itemId],
  undo: async (ctx) => {
    await deleteCanvasItem(deps.storyId, ctx.resolve(args.itemId));
    refreshCanvas(deps, ctx.resolve(args.itemId));
  },
  redo: async (ctx) => {
    if (args.recreate) {
      ctx.alias(args.itemId, await args.recreate());
    } else {
      await repinCanvasItem(deps.workspaceId, deps.storyId, ctx.resolve(args.itemId), args.position, deps.version);
    }
    refreshCanvas(deps, ctx.resolve(args.itemId));
  },
});

/**
 * Delete is an unpin on the new path, which keeps the canvas row: undo re-pins the same id at its
 * old geometry. The legacy path soft-deletes with no restore, so it is a barrier.
 */
export const deletedItemEntry = (
  deps: InverseDeps,
  args: { itemId: string; position: ItemPositionApi | null },
): HistoryEntryInput => {
  const label = "Delete tile";
  if (!isNewPath(deps.version) || !isNewPathDeleteUndoable()) return barrierEntry(label);
  return {
    label,
    itemIds: [args.itemId],
    undo: async (ctx) => {
      await repinCanvasItem(deps.workspaceId, deps.storyId, ctx.resolve(args.itemId), args.position, deps.version);
      refreshCanvas(deps, ctx.resolve(args.itemId));
    },
    redo: async (ctx) => {
      await deleteCanvasItem(deps.storyId, ctx.resolve(args.itemId));
      refreshCanvas(deps, ctx.resolve(args.itemId));
    },
  };
};

// ─── Rows 6, 7: content and source ───────────────────────────────────────────

/** What a PATCH .../content must carry to put an item's content back as the widget holds it now. */
export const contentBodyOf = (widget: CanvasWidgetApi | undefined): CanvasContentItemUpdateRequestApi | null => {
  if (!widget) return null;
  if (widget.type !== "link" && widget.type !== "embed" && widget.type !== "image" && widget.type !== "text") return null;
  return { item_type: widget.type, content: widget.content, metadata: { ...widget.metadata } };
};

export const contentEditEntry = (
  deps: InverseDeps,
  itemId: string,
  before: CanvasContentItemUpdateRequestApi,
  after: CanvasContentItemUpdateRequestApi,
): HistoryEntryInput => {
  const write = (body: CanvasContentItemUpdateRequestApi) => async (ctx: { resolve: (id: string) => string }) => {
    const id = ctx.resolve(itemId);
    const updated = await updateCanvasContentItem(deps.workspaceId, deps.storyId, id, body, deps.version);
    deps.queryClient.setQueryData(queryKeys.stories.canvasWidget(deps.workspaceId, deps.storyId, id), updated);
    refreshStoryItems(deps.queryClient, deps.storyId, id);
  };
  return { label: "Edit text", coalesceKey: `content:${itemId}`, itemIds: [itemId], undo: write(before), redo: write(after) };
};

export const sourceEditEntry = (
  deps: InverseDeps,
  itemId: string,
  before: CanvasItemSourceUpdateRequestApi,
  after: CanvasItemSourceUpdateRequestApi,
): HistoryEntryInput => {
  const write = (body: CanvasItemSourceUpdateRequestApi) => async (ctx: { resolve: (id: string) => string }) => {
    const id = ctx.resolve(itemId);
    const updated = await updateCanvasItemSource(deps.workspaceId, deps.storyId, id, body, deps.version);
    deps.queryClient.setQueryData(queryKeys.stories.canvasWidget(deps.workspaceId, deps.storyId, id), updated);
    refreshCanvas(deps, id);
  };
  return { label: "Change source", itemIds: [itemId], undo: write(before), redo: write(after) };
};

// ─── Row 8: summary / narrative visibility and text ──────────────────────────

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === "object" ? (value as Record<string, unknown>) : {});

/**
 * What a summary / narrative edit reads to find its inverse. A widget the canvas holds, else on
 * the new path a chart_2's own fields (flags as the tile shows them, and the text, hidden or not).
 */
export const readCachedCompanionSource = (deps: InverseDeps, itemId: string): CanvasWidgetApi | Record<string, unknown> | undefined => {
  const widget = readCachedWidget(deps, itemId);
  if (widget || !isNewPath(deps.version)) return widget;
  const item = deps.queryClient.getQueryData<CanvasItemFullApi>(queryKeys.storyItems.detail(deps.storyId, itemId));
  if (!item) return undefined;
  const fields = toCompanionFields(item, "new");
  return {
    visibility: { is_summary_visible: fields.isSummaryVisible, is_narrative_visible: fields.isNarrativeVisible },
    ...(fields.aiSummary ? { ai_summary: fields.aiSummary } : {}),
    ...(fields.narrative !== undefined ? { narrative: fields.narrative } : {}),
  };
};

/**
 * The visibility PATCH that puts back what `applied` changes, read from the widget as it is now.
 * Null when there is no widget to read: the caller records a barrier. With `requireText` (the new
 * path), also null when the edit writes words and the item holds none to put back.
 */
export const visibilityBefore = (
  widget: CanvasWidgetApi | Record<string, unknown> | undefined,
  applied: CanvasItemVisibilityUpdateRequest,
  requireText = false,
): CanvasItemVisibilityUpdateRequest | null => {
  if (!widget) return null;
  const current = asRecord(widget);
  const before: CanvasItemVisibilityUpdateRequest = {};
  if (applied.visibility) {
    const flags = asRecord(current.visibility);
    before.visibility = Object.fromEntries(
      Object.keys(applied.visibility).map((flag) => [flag, flags[flag] === true]),
    );
  }
  if (applied.ai_summary && current.ai_summary) before.ai_summary = current.ai_summary as CanvasItemVisibilityUpdateRequest["ai_summary"];
  if (applied.narrative !== undefined && typeof current.narrative === "string") before.narrative = current.narrative;
  if (applied.body !== undefined) {
    if (typeof current.body !== "string") return null;
    before.body = current.body;
    if (typeof current.format === "string") before.format = current.format;
  }
  if (requireText && ((applied.ai_summary && !before.ai_summary) || (applied.narrative !== undefined && before.narrative === undefined))) {
    return null;
  }
  return before;
};

export const visibilityEntry = (
  deps: InverseDeps,
  args: { label: string; itemId: string; before: CanvasItemVisibilityUpdateRequest; after: CanvasItemVisibilityUpdateRequest },
): HistoryEntryInput => {
  const write = (body: CanvasItemVisibilityUpdateRequest) => async (ctx: { resolve: (id: string) => string }) => {
    const id = ctx.resolve(args.itemId);
    await updateCanvasItemVisibility(deps.workspaceId, deps.storyId, id, body, deps.version);
    refreshCanvas(deps, id);
  };
  return { label: args.label, coalesceKey: `visibility:${args.itemId}`, itemIds: [args.itemId], undo: write(args.before), redo: write(args.after) };
};

// ─── Row 9: improve a summary or narrative ───────────────────────────────────

export type NarrativeScopeTarget =
  | { scope: "story" }
  | { scope: "storySummary"; widgetId: string }
  | { scope: "item"; itemId: string }
  | { scope: "itemSummary"; itemId: string };

/** The text a narrative target shows now, read from the cached widget; null when not cached. */
export const readNarrativeText = (deps: InverseDeps, target: NarrativeScopeTarget): string | null => {
  if (target.scope === "story") {
    // The story's narrative card: its widget id isn't in the target, so find the narrative widget.
    const cached = deps.queryClient.getQueriesData<CanvasWidgetApi>({
      queryKey: ["stories", deps.workspaceId, deps.storyId, "canvas", "widget"],
    });
    const card = cached.map(([, data]) => data).find((data) => data?.type === "narrative");
    return card && card.type === "narrative" ? card.body : null;
  }
  const id = target.scope === "storySummary" ? target.widgetId : target.itemId;
  const widget = asRecord(readCachedWidget(deps, id));
  if (target.scope === "itemSummary") {
    const body = asRecord(widget.ai_summary).body;
    return typeof body === "string" ? body : null;
  }
  if (target.scope === "item") return typeof widget.narrative === "string" ? widget.narrative : null;
  return typeof widget.body === "string" ? widget.body : null;
};

const putNarrativeText = (deps: InverseDeps, target: NarrativeScopeTarget, text: string): Promise<unknown> => {
  const { workspaceId: ws, storyId: story } = deps;
  switch (target.scope) {
    case "story": return updateStoryNarrative(ws, story, { narrative: text });
    case "storySummary": return updateStorySummary(ws, story, { text });
    case "item": return updateCanvasItemNarrative(ws, story, target.itemId, { narrative: text });
    case "itemSummary": return updateCanvasItemSummary(ws, story, target.itemId, { text });
  }
};

/**
 * Improve may answer before the new text exists (the summary is written in the background), so the
 * new text is read when the undo runs, and only then put back by the redo. No text then: the redo
 * fails with a toast rather than recording a barrier.
 */
export const improvedNarrativeEntry = (
  deps: InverseDeps,
  target: NarrativeScopeTarget,
  before: string,
): HistoryEntryInput => {
  let after: string | null = null;
  const refresh = () => {
    void deps.queryClient.invalidateQueries({ queryKey: ["stories", deps.workspaceId, deps.storyId, "canvas"] });
    refreshStoryItems(deps.queryClient, deps.storyId);
  };
  return {
    label: target.scope === "itemSummary" || target.scope === "storySummary" ? "Improve summary" : "Improve narrative",
    itemIds: target.scope === "item" || target.scope === "itemSummary" ? [target.itemId] : undefined,
    undo: async () => {
      // Still the old text: the background write has not landed, so there is nothing to redo.
      const read = readNarrativeText(deps, target);
      after = read === before ? null : read;
      await putNarrativeText(deps, target, before);
      refresh();
    },
    redo: async () => {
      if (after === null) throw new Error("The improved text is not available");
      await putNarrativeText(deps, target, after);
      refresh();
    },
  };
};

// ─── Row 10: rewrite text (new path) ─────────────────────────────────────────

/** The words of a new-path text tile, once it is up to date; null while rewriting or not written. */
export const readCurrentText = (deps: InverseDeps, itemId: string): string | null => {
  const text = deps.queryClient.getQueryData<CanvasItemFullApi>(queryKeys.storyItems.detail(deps.storyId, itemId))?.text;
  return text && text.state === "current" && typeof text.body === "string" ? text.body : null;
};

const changeText = async (deps: InverseDeps, itemId: string, text: string): Promise<void> => {
  const requestId = await postStoryEdit(deps.storyId, { kind: "change_text", item_id: itemId, text });
  await waitStoryEdit(deps.storyId, requestId);
  refreshStoryItems(deps.queryClient, deps.storyId, itemId);
};

export const rewrittenTextEntry = (deps: InverseDeps, itemId: string, before: string): HistoryEntryInput => {
  let after: string | null = null;
  return {
    label: "Rewrite text",
    itemIds: [itemId],
    undo: async (ctx) => {
      const id = ctx.resolve(itemId);
      const read = readCurrentText(deps, id);
      after = read === before ? null : read;
      await changeText(deps, id, before);
    },
    redo: async (ctx) => {
      if (after === null) throw new Error("The rewritten text is not available");
      await changeText(deps, ctx.resolve(itemId), after);
    },
  };
};

// ─── Row 11: story filters ───────────────────────────────────────────────────

export type ApplyFilterResult = (result: StoryFilterMutationResponseApi) => void;

type FilterWrite =
  | { type: "create"; body: StoryFilterCreateRequestApi }
  | { type: "patch"; filterId: string; body: StoryFilterPatchRequestApi }
  | { type: "remove"; filterId: string }
  | { type: "rewind"; toFilterId: string | null; expectedLatestId: string | null };

/** A body that makes this row again; null for a row a body cannot rebuild (drill step, baseline). */
export const createBodyFromFilter = (row: StoryFilterApi): StoryFilterCreateRequestApi | null => {
  if (row.kind === "global") return { kind: "global", field: row.field, comparator: row.comparator, value: row.value };
  if (row.kind === "cross") {
    if (row.comparator !== "in" && row.comparator !== "not_in" && row.comparator !== "between") return null;
    return { kind: "cross", field: row.field, comparator: row.comparator, value: row.value };
  }
  if (row.kind === "local" && row.canvas_item_id) {
    return { kind: "local", canvas_item_id: row.canvas_item_id, field: row.field, comparator: row.comparator, value: row.value };
  }
  return null;
};

const latestTrailId = (filters: readonly StoryFilterApi[]): string | null => {
  const trail = filters.filter((row) => row.seq !== null && row.kind !== "baseline" && row.kind !== "local");
  return trail.length > 0 ? trail[trail.length - 1].id : null;
};

/**
 * The patch that puts a row back as it was, in one step: its comparator and value, plus the old
 * field when the write moved the field, or the old drill target when it moved a drill's. A drill
 * step's target is its last extra condition's field, and its grain is re-derived from that.
 */
const toPatchInverse = (row: StoryFilterApi, body: StoryFilterPatchRequestApi): StoryFilterPatchRequestApi => {
  const inverse: StoryFilterPatchRequestApi = { comparator: row.comparator, value: row.value };
  if (body.field) inverse.field = row.field;
  if (body.drill_to) inverse.drill_to = row.also?.[row.also.length - 1]?.field ?? row.field;
  return inverse;
};

/**
 * One successful filter write. `before` is the list as it stood before it (the cache the write
 * replaces), `result` what the server answered. Answers a barrier when the write cannot be
 * rebuilt, such as one that also removed rows as a side effect or a drill step that was removed.
 */
export const filterWriteEntry = (
  deps: InverseDeps,
  apply: ApplyFilterResult,
  write: FilterWrite,
  before: StoryFilterListResponseApi | undefined,
  result: StoryFilterMutationResponseApi,
): HistoryEntryInput => {
  const { storyId } = deps;
  const run = async (call: Promise<StoryFilterMutationResponseApi>) => apply(await call);
  const beforeRows = before?.filters ?? [];

  if (write.type === "create") {
    const created = result.filter;
    if (!created || result.deleted_filter_ids.length > 0) return barrierEntry("Add filter");
    return {
      label: "Add filter",
      undo: (ctx) => run(deleteStoryFilter(storyId, ctx.resolve(created.id))),
      redo: async (ctx) => {
        const next = await createStoryFilter(storyId, write.body);
        if (next.filter) ctx.alias(created.id, next.filter.id);
        apply(next);
      },
    };
  }

  if (write.type === "patch") {
    const row = beforeRows.find((candidate) => candidate.id === write.filterId);
    if (!row) return barrierEntry("Change filter");
    return {
      label: "Change filter",
      undo: (ctx) => run(patchStoryFilter(storyId, ctx.resolve(write.filterId), toPatchInverse(row, write.body))),
      redo: (ctx) => run(patchStoryFilter(storyId, ctx.resolve(write.filterId), write.body)),
    };
  }

  // remove and rewind drop rows: put the same rows back, oldest first.
  const afterIds = new Set(result.filters.map((row) => row.id));
  const dropped = beforeRows
    .filter((row) => !afterIds.has(row.id))
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const bodies = dropped.map(createBodyFromFilter);
  const label = write.type === "remove" ? "Remove filter" : "Clear filters";
  if (dropped.length === 0 || bodies.some((body) => body === null)) return barrierEntry(label);

  const recreate = async (ctx: { alias: (oldId: string, newId: string) => void }) => {
    for (let index = 0; index < dropped.length; index += 1) {
      const next = await createStoryFilter(storyId, bodies[index] as StoryFilterCreateRequestApi);
      if (next.filter) ctx.alias(dropped[index].id, next.filter.id);
      apply(next);
    }
  };
  if (write.type === "remove") {
    return {
      label,
      undo: recreate,
      redo: (ctx) => run(deleteStoryFilter(storyId, ctx.resolve(write.filterId))),
    };
  }
  return {
    label,
    undo: recreate,
    redo: async (ctx) => {
      const list = deps.queryClient.getQueryData<StoryFilterListResponseApi>(queryKeys.storyFilters.list(storyId));
      await run(
        rewindStoryFilters(storyId, {
          to_filter_id: write.toFilterId === null ? null : ctx.resolve(write.toFilterId),
          expected_latest_id: latestTrailId(list?.filters ?? []),
        }),
      );
    },
  };
};

// ─── Row 12: rename the story ────────────────────────────────────────────────

export const renameStoryEntry = (deps: InverseDeps, before: string, after: string): HistoryEntryInput => {
  const write = (title: string) => async () => {
    const meta = await updateStoryMeta(deps.workspaceId, deps.storyId, { title });
    deps.queryClient.setQueryData<StoryMetaApi>(queryKeys.storyMeta.detail(deps.workspaceId, deps.storyId), meta);
  };
  return { label: "Rename story", coalesceKey: "story-title", undo: write(before), redo: write(after) };
};

// ─── Row 15: old-path chart edit (merge-patch) ───────────────────────────────

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The RFC 7396 merge-patch that undoes `patch` on `previous`: a key the patch changes is set back
 * to what it held, and `null` removes one that did not exist. Objects recurse; arrays are whole.
 */
export const inverseMergePatch = (previous: unknown, patch: unknown): unknown => {
  if (!isPlainObject(patch)) return previous === undefined ? null : previous;
  const prior = isPlainObject(previous) ? previous : undefined;
  const inverse: Record<string, unknown> = {};
  Object.keys(patch).forEach((key) => {
    const next = patch[key];
    const was = prior?.[key];
    inverse[key] = isPlainObject(next) && isPlainObject(was) ? inverseMergePatch(was, next) : was === undefined ? null : was;
  });
  return inverse;
};

/** The PATCH body that undoes `body` on the item as it was; null when it cannot be told. */
export const inverseItemPatch = (
  previous: CanvasItemFullApi | undefined,
  body: CanvasItemPatchRequestApi,
): CanvasItemPatchRequestApi | null => {
  if (!previous) return null;
  const inverse: CanvasItemPatchRequestApi = {};
  if (body.binding !== undefined) inverse.binding = inverseMergePatch(previous.binding, body.binding) as CanvasItemPatchRequestApi["binding"];
  if (body.format !== undefined) inverse.format = inverseMergePatch(previous.format, body.format) as CanvasItemPatchRequestApi["format"];
  if (body.query_model !== undefined) {
    if (!previous.query_model) return null;
    inverse.query_model = { model_release_id: previous.query_model.model_release_id };
  }
  if (body.chart !== undefined) {
    if (!previous.look) return null;
    inverse.chart = { fields: previous.fields ?? undefined, look: previous.look };
  }
  return inverse;
};

export const itemPatchEntry = (
  deps: InverseDeps,
  itemId: string,
  before: CanvasItemPatchRequestApi,
  after: CanvasItemPatchRequestApi,
): HistoryEntryInput => {
  const write = (body: CanvasItemPatchRequestApi) => async (ctx: { resolve: (id: string) => string }) => {
    const id = ctx.resolve(itemId);
    const item = await patchCanvasItem(deps.storyId, id, body);
    deps.queryClient.setQueryData(queryKeys.storyItems.detail(deps.storyId, id), item);
    refreshStoryItems(deps.queryClient, deps.storyId);
  };
  return { label: "Edit chart", coalesceKey: `chart:${itemId}`, itemIds: [itemId], undo: write(before), redo: write(after) };
};
